import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePanelSectionAccess } from '../hooks/usePanelSectionAccess';
import { canAccessSalonRefSection } from '../panelAccess/effectivePanelAccess';
import { getCodeDefaultPanelAccess } from '../panelAccess/panelAccessDefaults';

export type PanelAccessContextValue = {
  loading: boolean;
  /** Individuel > défaut rôle > défaut code (+ fonctions référent pour salon_ref). */
  hasAccessToSection: (sectionId: string) => boolean;
  referentFunctions: string[];
  hasReferentFunction: (referentFunction: string) => boolean;
};

const PanelAccessContext = createContext<PanelAccessContextValue | null>(null);

/**
 * Active le chargement des droits panel uniquement sur les chemins /panel*.
 */
export const PanelAccessBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const enabled = location.pathname.startsWith('/panel');
  const { loading, hasAccessToSection, hasReferentFunction, referentFunctions } = usePanelSectionAccess(enabled);

  const value = useMemo(
    () => ({
      loading,
      hasAccessToSection,
      hasReferentFunction,
      referentFunctions,
    }),
    [loading, hasAccessToSection, hasReferentFunction, referentFunctions],
  );

  return <PanelAccessContext.Provider value={value}>{children}</PanelAccessContext.Provider>;
};

export function usePanelAccess(): PanelAccessContextValue {
  const ctx = useContext(PanelAccessContext);
  if (!ctx) {
    throw new Error('usePanelAccess doit être utilisé sous <PanelAccessBoundary> (routes /panel).');
  }
  return ctx;
}

export function usePanelAccessOptional(): PanelAccessContextValue | null {
  return useContext(PanelAccessContext);
}

/** Repli pour PanelSectionGuard pendant le chargement (sans fonctions référent). */
export function fallbackSectionAccess(sectionId: string, roleId: number): boolean {
  if (sectionId === 'salon_ref') {
    return canAccessSalonRefSection(roleId, [], [], []);
  }
  return getCodeDefaultPanelAccess(sectionId, roleId);
}
